import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Slider,
  Switch,
  FormControlLabel,
  TextField,
  IconButton,
  LinearProgress,
  Grid,
  Stack,
  Snackbar,
  Alert,
  Tooltip,
  Divider,
  Chip,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import LockIcon from '@mui/icons-material/Lock';

const STORAGE_KEY = 'ezprocessing.passwordGenerator.settings.v1';

const CHAR_SETS = {
  uppercase: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
  lowercase: 'abcdefghijklmnopqrstuvwxyz',
  numbers: '0123456789',
  symbols: '!@#$%^&*()-_=+[]{};:,.<>?/~',
};

const DEFAULT_SETTINGS = {
  length: 16,
  uppercase: true,
  lowercase: true,
  numbers: true,
  symbols: true,
};

function randomInt(max) {
  return Math.floor(Math.random() * max);
}

function pickFrom(str) {
  return str.charAt(randomInt(str.length));
}

function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = randomInt(i + 1);
    const tmp = a[i];
    a[i] = a[j];
    a[j] = tmp;
  }
  return a;
}

function buildPassword(settings) {
  const activeKeys = ['uppercase', 'lowercase', 'numbers', 'symbols'].filter(
    (k) => settings[k]
  );
  if (activeKeys.length === 0) {
    return '';
  }

  const pool = activeKeys.map((k) => CHAR_SETS[k]).join('');
  const length = Math.max(settings.length, activeKeys.length);

  // Guarantee at least one character from each active set.
  const chars = activeKeys.map((k) => pickFrom(CHAR_SETS[k]));
  while (chars.length < length) {
    chars.push(pickFrom(pool));
  }

  return shuffle(chars).join('');
}

function loadSettings() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    const parsed = JSON.parse(raw);
    return {
      length:
        typeof parsed.length === 'number'
          ? Math.min(48, Math.max(8, parsed.length))
          : DEFAULT_SETTINGS.length,
      uppercase: !!parsed.uppercase,
      lowercase: !!parsed.lowercase,
      numbers: !!parsed.numbers,
      symbols: !!parsed.symbols,
    };
  } catch (e) {
    return DEFAULT_SETTINGS;
  }
}

function evaluateStrength(password, settings) {
  if (!password) {
    return { score: 0, value: 0, label: 'None', color: 'error' };
  }

  const variety =
    (settings.uppercase ? 1 : 0) +
    (settings.lowercase ? 1 : 0) +
    (settings.numbers ? 1 : 0) +
    (settings.symbols ? 1 : 0);

  let score = 0;

  // Length contribution.
  if (password.length >= 12) score += 1;
  if (password.length >= 16) score += 1;
  if (password.length >= 24) score += 1;

  // Variety contribution.
  if (variety >= 2) score += 1;
  if (variety >= 3) score += 1;
  if (variety >= 4) score += 1;

  // Cap and map.
  if (score > 4) score = 4;
  if (password.length < 8) score = Math.min(score, 1);

  const map = {
    0: { label: 'Weak', color: 'error', value: 20 },
    1: { label: 'Weak', color: 'error', value: 25 },
    2: { label: 'Fair', color: 'warning', value: 50 },
    3: { label: 'Good', color: 'info', value: 75 },
    4: { label: 'Strong', color: 'success', value: 100 },
  };

  const result = map[score];
  return { score, value: result.value, label: result.label, color: result.color };
}

export default function PasswordGeneratorPage() {
  const [settings, setSettings] = useState(loadSettings);
  const [password, setPassword] = useState('');
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  const anyActive =
    settings.uppercase || settings.lowercase || settings.numbers || settings.symbols;

  const regenerate = useCallback(() => {
    setPassword(buildPassword(settings));
  }, [settings]);

  // Persist settings.
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch (e) {
      /* ignore persistence errors */
    }
  }, [settings]);

  // Regenerate whenever settings change (and on mount).
  useEffect(() => {
    setPassword(buildPassword(settings));
  }, [settings]);

  const strength = useMemo(
    () => evaluateStrength(password, settings),
    [password, settings]
  );

  const handleToggle = (key) => (event) => {
    const next = { ...settings, [key]: event.target.checked };
    const stillActive =
      next.uppercase || next.lowercase || next.numbers || next.symbols;
    if (!stillActive) {
      // Prevent turning everything off.
      setSnack({
        open: true,
        message: 'At least one character type must stay enabled.',
        severity: 'warning',
      });
      return;
    }
    setSettings(next);
  };

  const handleLengthChange = (event, value) => {
    setSettings((prev) => ({ ...prev, length: value }));
  };

  const handleCopy = async () => {
    if (!password) return;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(password);
        setSnack({ open: true, message: 'Password copied to clipboard.', severity: 'success' });
      } else {
        setSnack({
          open: true,
          message: 'Clipboard not available in this browser.',
          severity: 'error',
        });
      }
    } catch (e) {
      setSnack({ open: true, message: 'Unable to copy password.', severity: 'error' });
    }
  };

  const handleCloseSnack = () => {
    setSnack((prev) => ({ ...prev, open: false }));
  };

  const toggles = [
    { key: 'uppercase', label: 'Uppercase (A-Z)' },
    { key: 'lowercase', label: 'Lowercase (a-z)' },
    { key: 'numbers', label: 'Numbers (0-9)' },
    { key: 'symbols', label: 'Symbols (!@#$)' },
  ];

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
        Password Generator
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Create strong, random passwords entirely in your browser. Nothing is sent anywhere.
      </Typography>

      <Grid container spacing={3}>
        {/* Result + strength */}
        <Grid item xs={12} md={7}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mb: 2 }}
              >
                <LockIcon color="primary" fontSize="small" />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Your password
                </Typography>
              </Stack>

              <TextField
                fullWidth
                value={password}
                placeholder="Enable a character type to generate"
                InputProps={{
                  readOnly: true,
                  sx: {
                    fontFamily:
                      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                    fontSize: '1.05rem',
                    letterSpacing: '0.5px',
                  },
                  endAdornment: (
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title="Copy">
                        <span>
                          <IconButton
                            aria-label="copy password"
                            onClick={handleCopy}
                            disabled={!password}
                            size="small"
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Regenerate">
                        <span>
                          <IconButton
                            aria-label="regenerate password"
                            onClick={regenerate}
                            disabled={!anyActive}
                            size="small"
                            color="primary"
                          >
                            <RefreshIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  ),
                }}
              />

              <Box sx={{ mt: 3 }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 1 }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Strength
                  </Typography>
                  <Chip
                    size="small"
                    label={strength.label}
                    color={strength.color}
                    variant="outlined"
                    sx={{ fontWeight: 600 }}
                  />
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={strength.value}
                  color={strength.color}
                  sx={{ height: 10, borderRadius: 5 }}
                />
              </Box>

              <Divider sx={{ my: 3 }} />

              <Typography variant="body2" color="text.secondary">
                Length: <strong>{settings.length}</strong> characters using{' '}
                {
                  ['uppercase', 'lowercase', 'numbers', 'symbols'].filter(
                    (k) => settings[k]
                  ).length
                }{' '}
                character set(s).
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Controls */}
        <Grid item xs={12} md={5}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Options
              </Typography>

              <Box sx={{ px: 1, mb: 1 }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 1 }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Length
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {settings.length}
                  </Typography>
                </Stack>
                <Slider
                  value={settings.length}
                  onChange={handleLengthChange}
                  min={8}
                  max={48}
                  step={1}
                  marks={[
                    { value: 8, label: '8' },
                    { value: 28, label: '28' },
                    { value: 48, label: '48' },
                  ]}
                  valueLabelDisplay="auto"
                  aria-label="password length"
                />
              </Box>

              <Divider sx={{ my: 2 }} />

              <Stack spacing={0.5}>
                {toggles.map((t) => (
                  <FormControlLabel
                    key={t.key}
                    control={
                      <Switch
                        checked={settings[t.key]}
                        onChange={handleToggle(t.key)}
                      />
                    }
                    label={t.label}
                  />
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar
        open={snack.open}
        autoHideDuration={2500}
        onClose={handleCloseSnack}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnack}
          severity={snack.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
